import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';

interface ExternalTextFileProps {
  scratchFile: GetAnalysisScratchFile;
}

function TextFileContent({signedURL}: {signedURL: string}): JSX.Element {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    net
      .getPromise(signedURL)
      .then(setTextContent)
      .catch(() => {
        setError('Failed to load text file. Please contact Benchling support if the problem persists.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [signedURL]);

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }

  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  return (
    <textarea
      className={css(styles.textArea)}
      value={textContent || ''}
      readOnly={true}
      placeholder="Empty file"
    />
  );
}

export default function ExternalTextFile({scratchFile}: ExternalTextFileProps): JSX.Element {
  return (
    <SignedExtFileURLProxy
      externalFileId={scratchFile.externalFileId!}
      url={scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading text file." />;
        } else {
          return <TextFileContent signedURL={signedURL} />;
        }
      }}
      owningEntityApiId={scratchFile.id}
    />
  );
}

const styles = StyleSheet.create({
  textArea: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
  },
});
